/**
 * Google Maps JavaScript API loader.
 * Loads the script exactly once per page session and exposes a promise
 * that resolves when `window.google.maps` is ready.
 *
 * Usage:
 *   import { loadGoogleMaps } from '../utils/googleMapsLoader';
 *   await loadGoogleMaps();
 */

let loadPromise = null;

export const loadGoogleMaps = () => {
  // Already loaded
  if (window.google?.maps) {
    return Promise.resolve();
  }

  // Already loading — return the same promise
  if (loadPromise) {
    return loadPromise;
  }

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    console.error(
      '[GoogleMaps] REACT_APP_GOOGLE_MAPS_API_KEY is not set. ' +
        'Map features will be unavailable.'
    );
    return Promise.reject(new Error('Google Maps API key is missing.'));
  }

  loadPromise = new Promise((resolve, reject) => {
    // Unique callback name to avoid collisions
    const callbackName = '__googleMapsInitCallback__';

    window[callbackName] = () => {
      delete window[callbackName];
      resolve();
    };

    const script = document.createElement('script');
    script.src =
      `https://maps.googleapis.com/maps/api/js` +
      `?key=${apiKey}` +
      `&libraries=places` +
      `&loading=async` +
      `&callback=${callbackName}` +
      `&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      loadPromise = null; // allow retry
      reject(new Error('Failed to load Google Maps script.'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};
